import { User } from '@alfresco/adf-core';

import { CKEditor5 } from '../components/ckeditor';
import { IKeywordDefinition } from '../store';
import { Range } from './comment.model';

type UNDO_REDO = 'undo' | 'redo';
export interface BlurEvent {
  event: CKEditor5.EventInfo<'blur'>;
  editor: CKEditor5.Editor;
}

export interface FocusEvent {
  event: CKEditor5.EventInfo<'focus'>;
  editor: CKEditor5.Editor;
}

export interface ChangeEvent {
  event: CKEditor5.EventInfo<'change:data'>;
  editor: CKEditor5.Editor;
}

export interface SaveEvent {
  event: CKEditor5.EventInfo<'save'>;
  editor: CKEditor5.Editor;
}

export interface CitationEvent {
  event: CKEditor5.EventInfo<'citation'>;
  editor: CKEditor5.Editor;
}
export interface ChevronNavEvent {
  event: CKEditor5.EventInfo<'chevronnav'>;
  editor: CKEditor5.Editor;
  id: string;
  citationCount: string;
}
export interface InsertSubTemplateEvt {
  event: CKEditor5.EventInfo<'insertSubTemplate'>;
  editor: CKEditor5.Editor;
  name: string;
}

export interface OpenContextMenuEvt {
  event: CKEditor5.EventInfo<'openContextMenu'>;
  editor: CKEditor5.Editor;
  contextMenuObj: [];
}

export interface MediaPropEvt {
  event: CKEditor5.EventInfo<'mediaPropertiesNotification'>;
  editor: CKEditor5.Editor;
  value: boolean;
}

export interface DiscardCitationEvt {
  event: CKEditor5.EventInfo<'discardCitation'>;
  editor: CKEditor5.Editor;
  value: { citationRefs: [] };
}

export interface CitationsDeleteFromDocumentEvent {
  event: CKEditor5.EventInfo<'citationsDeleteFromDocument'>;
  editor: CKEditor5.Editor;
  multipleRefs: [];
  isCut: boolean;
}

export interface CitationsPanelReOrder {
  event: CKEditor5.EventInfo<'citationsPanelReOrder'>;
  editor: CKEditor5.Editor;
  id: any;
  citationsPanelReOrderData: {
    deletedRefId: string;
    newFirstCitedRefId: string;
    subsequentRefIds: string[];
  }[];
}

export interface ReOrderRightHandPanel {
  event: CKEditor5.EventInfo<'reOrderRightHandPanel'>;
  editor: CKEditor5.Editor;
}

export interface CitationsPanelReOrderOnSubRefDeletion {
  event: CKEditor5.EventInfo<'citationsPanelReOrderOnSubRefDeletion'>;
  editor: CKEditor5.Editor;
}

export interface NewCustomGuidEvt {
  event: CKEditor5.EventInfo<'newCustomGuid'>;
  editor: CKEditor5.Editor;
  id: string;
}

export interface PasteCitationRefsInDocument {
  event: CKEditor5.EventInfo<'pasteCitationRefsInDocument'>;
  editor: CKEditor5.Editor;
  refsToBeReOrderedInRightHandPanel: [];
}

export interface CommentStatusThreadIdEvent {
  commentStatusThreadIdStatus: {
    threadId: string;
    status: string;
  };
}

export interface InsertMediaEvent {
  event: CKEditor5.EventInfo<'insertmedia'>;
  editor: CKEditor5.Editor;
  name: string;
}

export interface InsertLinkEvent {
  editor: CKEditor5.Editor;
  arguments: CKEditor5.EventInfo<'insertlink'>;
  linkAttributes: any;
}

export interface InsertWebEmailLinkEvent {
  value: boolean;
}
export interface CopyCitationNotificationEvent {
  event: CKEditor5.EventInfo<'copyCitationNotification'>;
  editor: CKEditor5.Editor;
}

export interface MediaSelectedEvt {
  event: CKEditor5.EventInfo<'mediaselected'>;
  editor: CKEditor5.Editor;
  mediaObj: [];
}

export interface LinkAnotherDocumentEvent {
  event: CKEditor5.EventInfo<''>;
  idlink: IdLink;
}

export interface IdLink {
  path: LinkDetails[];
}

export interface LinkDetails {
  linkAddress: string;
}

export interface InsertLinkToAnotherDocumentEvent {
  event: CKEditor5.EventInfo<'insertlinktoanotherdocument'>;
  editor: CKEditor5.Editor;
  linkAttributes: any;
}

export interface CitationUndoRedoEvent {
  event: CKEditor5.EventInfo<'citationundoredo'>;
  editor: CKEditor5.Editor;
  linkAttributes: {
    citationRef: any;
    operation: string;
    type: string;
    id: string;
  };
}

export interface ReOrderUnNumberedReferencesEvent {
  event: CKEditor5.EventInfo<'reorderunnumberedreferences'>;
  editor: CKEditor5.Editor;
}

export interface RemoveMedia {
  event: CKEditor5.EventInfo<'removemedia'>;
  editor: CKEditor5.Editor;
}

export interface CustomComment {
  attributes: Record<string, any>;
  author: User;
  authoredAt: Date;
  content: string;
  createdAt: Date;
  creator: User;
  id: string;
  isEditable: boolean;
  isExternal: boolean;
  isReadOnly: boolean;
  isRemovable: boolean;
  resolvedAt: null | Date;
  threadId: string;
  weight: number;
  commentId?: string;
}
export interface CustomCommentThread {
  attributes: Record<string, unknown>;
  channelId: string | symbol;
  comments: CustomComment[];
  isAttached: boolean;
  isReadOnly: boolean;
  isRemovable: boolean;
  isResolved: boolean;
  isSubmitted: boolean;
  length: number;
  resolvedAt: null | Date;
  weight: number;
  threadId?: string;
  id?: string;
  remove: () => {};
}

export interface CustomCommentsRepository {
  getCommentThreads: () => [{}];
}

export interface UndoInterface {
  event: CKEditor5.EventInfo<'undoRedoExecuted'>;
  editor: CKEditor5.Editor;
}

export interface RouteToLinkEvent {
  event: CKEditor5.EventInfo<'routeToLink'>;
  linkAttributes: any;
}

export interface CommonEvent {
  target: CommonTarget;
}

export interface CommonTarget {
  innerText: string;
}
export interface RemoveInsertedCitationsOnDiscardEvent {
  event: CKEditor5.EventInfo<'removeInsertedCitationsOnDiscard'>;
}

export interface InsertReferenceEvent {
  editor: CKEditor5.Editor;
  arguments: CKEditor5.EventInfo<'insertreference'>;
  refAttributes: any;
}

export interface RemoveInsertedKeywordOnDiscardEvent {
  event: CKEditor5.EventInfo<'removeInsertedKeywordOnDiscard'>;
  docRefId: string;
  keywordId: string;
}

export interface DiscardForKeyword {
  keywordId: string;
  docReferenceId: string;
}

export interface CopyKeywordFromKeyboardEvent {
  arguments: HTMLElement;
}

export interface KeywordUndoRedoEvent {
  operation: UNDO_REDO;
  keywordToUndo: DiscardForKeyword[];
  keywordToRedo: DiscardForKeyword[];
}

export interface ToggleEvent extends Event {
  currentTarget: HTMLElement;
}

export interface DeleteKeywordEvent {
  event: CKEditor5.EventInfo<'keywordDeleteFromDocument'>;
  editor: CKEditor5.Editor;
  keywordRefs: IKeywordDefinition[];
  unwrapKeyword: boolean;
  isEnabledDialog: boolean;
  selectedKeywordId: string[];
}

export interface NumberedCitationInTableEvent {
  event: CKEditor5.EventInfo<'checkNumberedCitationInTable'>;
  selectedElement: Element;
}

export interface RemoveKeywordFromSubtemplateEvent {
  resourceId: string;
  keywordId: string;
}

export type SuggestionType = 'format' | 'replace' | 'deletion' | 'insertion';

export interface ISuggestion {
  attributes: {
    uniqueId: string;
  };
  authorId: string;
  createdAt: string;
  data:
    | object
    | {
        key?: string;
        oldValue?: unknown;
        newValue?: unknown;
        commandName?: string;
        commandParams?: unknown[];
      };
  hasComments: boolean;
  id: string;
  type: SuggestionType;
  subType: string;
}

export interface SaveCommentThreadModel {
  threadId: string;
  comments: CustomComment[];
}

export interface TrackChangesAndComments {
  suggestions: ISuggestion[];
  commentThreads: SaveCommentThreadModel[];
}

export interface ISideBarView {
  view: {
    element: HTMLElement;
  };
}

export interface TrackChangeTable {
  isColumnInsertion?: boolean;
  isColumnDeletion?: boolean;
  isTableInsertion?: boolean;
  isTableDeletion?: boolean;
  isRowInsertion?: boolean;
  isRowDeletion?: boolean;
}
export interface RemoveCommentsAndReleaseNotesEvent {
  range: Range;
}

export interface MessageInfoEvent {
  isBulletListCopyMessage?: boolean;
  isBulletListCutMessage?: boolean;
}

export interface ExternalPasteConfirmationRequiredEvent {
  plainText: string;
  pasteSource: string;
}

export interface ReorderedFinalReferencesEvent {
  idsOfFirstCitedRefs: string[];
}
